import { useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { ChatWidget } from "./components/ChatWidget";
import { CookieBanner } from "./components/CookieBanner";
import { Analytics } from "./components/Analytics";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Frontaliers from "./pages/Frontaliers";
import Entreprises from "./pages/Entreprises";
import Devis from "./pages/Devis";
import { Confidentialite, MentionsLegales, ConditionsGenerales } from "./pages/LegalPages";
import Faq from "./pages/Faq";
import GuidesHub from "./pages/guides/GuidesHub";
import GuideCategory from "./pages/guides/GuideCategory";
import GuidePage from "./pages/guides/GuidePage";
import { MessageCircle } from "lucide-react";

const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Admin = lazy(() => import("./pages/Admin"));
const ClientAuth = lazy(() => import("./pages/ClientAuth"));
const EspaceClient = lazy(() => import("./pages/EspaceClient"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Outils = lazy(() => import("./pages/outils/Outils"));
const QuasiResidentSimulator = lazy(() => import("./pages/outils/QuasiResidentSimulator"));
const FraisReels = lazy(() => import("./pages/outils/FraisReels"));
const Checklist = lazy(() => import("./pages/outils/Checklist"));
const Calc90 = lazy(() => import("./pages/outils/Calculateurs").then((m) => ({ default: m.Calc90 })));
const Convertisseur = lazy(() => import("./pages/outils/Calculateurs").then((m) => ({ default: m.Convertisseur })));

const Loader = () => <div className="pt-[76px] min-h-[60vh]" />;

const Layout = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <>
      <Analytics />
      {!isAdmin && <Navbar onOpenChat={() => setChatOpen(true)} />}
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/" element={<Home onOpenChat={() => setChatOpen(true)} />} />
          <Route path="/services" element={<Services />} />
          <Route path="/frontaliers" element={<Frontaliers />} />
          <Route path="/independants-entreprises" element={<Entreprises />} />
          <Route path="/devis" element={<Devis />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/guides-fiscaux-frontaliers" element={<GuidesHub />} />
          <Route path="/guides-fiscaux-frontaliers/:category" element={<GuideCategory />} />
          <Route path="/outils-frontaliers" element={<Outils />} />
          <Route path="/outils-frontaliers/simulateur-quasi-resident" element={<QuasiResidentSimulator />} />
          <Route path="/outils-frontaliers/calculateur-90-pourcent" element={<Calc90 />} />
          <Route path="/outils-frontaliers/convertisseur-chf-eur" element={<Convertisseur />} />
          <Route path="/outils-frontaliers/simulateur-frais-reels" element={<FraisReels />} />
          <Route path="/outils-frontaliers/checklist-declaration-frontalier" element={<Checklist slug="checklist-declaration-frontalier" />} />
          <Route path="/outils-frontaliers/checklist-tou" element={<Checklist slug="checklist-tou" />} />
          <Route path="/outils-frontaliers/checklist-documents-fiscaux" element={<Checklist slug="checklist-documents-fiscaux" />} />
          <Route path="/espace-client/connexion" element={<ClientAuth />} />
          <Route path="/espace-client" element={<EspaceClient />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
          <Route path="/politique-de-confidentialite" element={<Confidentialite />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/conditions-generales" element={<ConditionsGenerales />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/:slug" element={<GuidePage />} />
        </Routes>
      </Suspense>
      {!isAdmin && <Footer />}
      {!isAdmin && !chatOpen && (
        <button
          data-testid="chat-fab"
          onClick={() => setChatOpen(true)}
          className="fixed bottom-5 right-5 z-[65] bg-[#0A1128] text-white rounded-full w-14 h-14 flex items-center justify-center shadow-[0_8px_24px_rgba(10,17,40,0.35)] hover:scale-105 transition-transform duration-200"
          aria-label="Ouvrir le chat conseiller fiscal IA"
        >
          <MessageCircle size={24} strokeWidth={1.75} />
        </button>
      )}
      <ChatWidget open={chatOpen} onClose={() => setChatOpen(false)} />
      {!isAdmin && <CookieBanner />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Layout />
      </BrowserRouter>
    </div>
  );
}

export default App;
